/**
 * Network (mediator) ad provider (ads epic — sc-6958).
 *
 * A single adapter over whichever gaming-native mediator we wire — AdinPlay,
 * GameMonetize, Playwire, Venatus. These are the RIGHT networks for a
 * canvas/iframe game PWA with incentivized traffic (AdSense is not: it can't
 * read iframe/canvas content and forbids incentivized + auto-refresh units).
 *
 * DELIBERATELY UNBOUND to a specific SDK. The concrete mediator is a fill-in
 * at the marked `TODO(network)` seams — loading its script, mapping its
 * banner/interstitial/rewarded calls — NOT an architecture change. Until a
 * mediator + creds are configured (AD_CONFIG.network.enabled), this provider
 * reports every format `unavailable` and the manager serves house ads only.
 * That's why the whole feature ships dark: no behavior change until flipped.
 *
 * The manager owns the paying-user gate; this provider never checks
 * entitlement. It just serves (or reports it can't).
 */

import type {
  AdContext,
  AdFormat,
  AdProvider,
  BannerHandle,
  InterstitialResult,
  RewardedResult,
} from './provider.js';

export interface NetworkProviderConfig {
  /** Mediator id, recorded on events/grants (e.g. 'adinplay'). */
  id: string;
  /** Master switch — false ⇒ every format unavailable (ships dark). */
  enabled: boolean;
  /** Mediator account/site id, env-injected. Empty until creds exist. */
  accountId: string;
  /** Optional per-format placement ids the mediator assigns. */
  placements?: Partial<Record<AdFormat, string>>;
}

export class NetworkAdProvider implements AdProvider {
  readonly id: string;
  private readonly cfg: NetworkProviderConfig;
  private sdkLoaded = false;

  constructor(cfg: NetworkProviderConfig) {
    this.cfg = cfg;
    this.id = cfg.id;
  }

  /**
   * Off, or missing creds ⇒ never ready. When wired, this should also
   * reflect the mediator SDK's real readiness/fill for `format`.
   */
  isReady(_format: AdFormat): boolean {
    if (!this.cfg.enabled || !this.cfg.accountId) return false;
    // TODO(network): return the mediator SDK's ready/fill state for _format,
    // e.g. `return window.<sdk>.isReady(this.cfg.placements?.[_format])`.
    return this.sdkLoaded;
  }

  /**
   * Lazy-load the mediator script once, on first use. Kept off the critical
   * path so a network provider never slows cabinet boot. No-op while dark.
   */
  private async ensureSdk(): Promise<boolean> {
    if (!this.cfg.enabled || !this.cfg.accountId) return false;
    if (this.sdkLoaded) return true;
    // TODO(network): inject the mediator <script> (once), await its ready
    // callback, then set this.sdkLoaded = true. Left unimplemented so the
    // provider stays inert until a concrete mediator is chosen.
    return false;
  }

  mountBanner(_host: HTMLElement, _ctx: AdContext): BannerHandle | null {
    if (!this.isReady('banner')) return null;
    // TODO(network): create the mediator's banner in `_host`, wire its
    // impression/click callbacks to recordAdEvent({ provider: this.id,
    // format: 'banner', ... }), and return a { tick, dispose } handle.
    return null;
  }

  async showInterstitial(_ctx: AdContext): Promise<InterstitialResult> {
    if (!(await this.ensureSdk())) return { status: 'unavailable' };
    // TODO(network): await the mediator's interstitial; resolve 'shown' when
    // it closes, 'unavailable' on no-fill/error.
    return { status: 'unavailable' };
  }

  async showRewarded(_ctx: AdContext): Promise<RewardedResult> {
    if (!(await this.ensureSdk())) return { status: 'unavailable' };
    // TODO(network): await the mediator's rewarded video. Resolve 'completed'
    // ONLY on the SDK's reward callback (the manager credits a token off that,
    // and the server re-caps it); 'dismissed' on early close; 'unavailable'
    // on no-fill/error. Pass adUnit: this.cfg.placements?.rewarded for the ledger.
    return { status: 'unavailable' };
  }
}
